/**
 * Auth Store
 * Manages user authentication state
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authAPI, referralAPI } from '../api/client';
import { identify, track } from '../utils/analytics';

export const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      isAuthenticated: false,
      loading: false,
      error: null,

      // Actions
      login: async (studentId, password, autoLogin = false) => {
        set({ loading: true, error: null });
        try {
          const response = await authAPI.login({ id: studentId, pass: password, autoLogin });

          if (response.success) {
            set({
              user: response.data,
              isAuthenticated: true,
              loading: false,
              error: null,
            });
            track('login', { method: autoLogin ? 'auto' : 'manual' });
            identify(response.data.userId, {
              studentId: response.data.studentId,
              name: response.data.name || null,
              plan: response.data.subscriptionStatus || null,
            });
            return { success: true, data: response.data };
          }
        } catch (error) {
          const errorMessage = error.response?.data?.error || error.message;
          set({ loading: false, error: errorMessage });
          track('login-failed', { error: errorMessage });
          return { success: false, error: errorMessage };
        }
      },

      logout: () => {
        set({
          user: null,
          isAuthenticated: false,
          error: null,
        });
      },

      clearError: () => {
        set({ error: null });
      },

      // 구독/접근 상태 갱신 (페이지 진입 시 호출)
      refreshAccess: async () => {
        const user = get().user;
        if (!user?.userId) return;
        try {
          const res = await referralAPI.getStatus(user.userId);
          if (res.success) {
            set({
              user: {
                ...get().user,
                accessUntil: res.data.accessUntil,
                subscriptionStatus: res.data.subscriptionStatus,
              },
            });
            identify(user.userId, {
              studentId: user.studentId,
              name: user.name || null,
              plan: res.data.subscriptionStatus || null,
            });
          }
        } catch { /* silent */ }
      },

      // window focus 시 자동 갱신 등록
      _initFocusRefresh: () => {
        if (typeof window === 'undefined') return;
        const handler = () => {
          const { user, isAuthenticated } = get();
          if (isAuthenticated && user?.userId) get().refreshAccess();
        };
        window.removeEventListener('focus', handler);
        window.addEventListener('focus', handler);
      },
    }),
    {
      name: 'auth-storage', // localStorage key
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
